import React from 'react';
import * as R from 'ramda';
import { Button, makeStyles } from '@material-ui/core';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { PosPlugin } from 'plugins/plugin';
import { modals } from 'plugins/mm/constants';
import { openPluginModalPage } from 'actions/modalPage';
import { reactcursively } from 'utils/react';

const useStyles = makeStyles(theme => ({
  [theme.breakpoints.down('sm')]: {
    buttonContainer: {
      width: '100%',
      display: 'flex',
      justifyContent: 'end',
      paddingRight: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
  },
  [theme.breakpoints.up('sm')]: {
    buttonContainer: {
      position: 'relative',
      width: '100%',
      height: 0,
    },
    button: {
      position: 'absolute',
      top: '-52px',
      left: '40%',
    },
  },
}));

const setFieldLabel = (fieldName: string, label: string) =>
  R.when(
    R.both(
      R.hasPath(['props', 'label']),
      R.pathEq(['props', 'name'], fieldName),
    ),
    R.set(R.lensPath(['props', 'label']), label),
  );

/**
 * Address search button should appear both in Customer form and in Other addresses.
 * Subtitle + extra buttons are not included in the AddressFormBeta, so to avoid
 * adding multiple new plugin components, address search button is inserted into
 * AddressFormBeta and positioned between subtitle and extra buttons using css.
 */
const WithAddressSearchButton: Required<PosPlugin>['UIAddressFormBeta'] = ({
  address,
  onChange,
  children,
}) => {
  const styles = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation('addressForm');

  const addAddressSearchButton = R.prepend(
    <div className={styles.buttonContainer}>
      <Button
        variant="contained"
        disableElevation
        className={styles.button}
        onClick={() =>
          dispatch(
            openPluginModalPage(modals.ADDRESS_SEARCH)({
              isPopup: true,
              props: { onChange },
            }),
          )
        }
      >
        {t('lookUpAddress.title')}
      </Button>
    </div>,
  );

  const setCustomTranslations = reactcursively(
    R.pipe(
      setFieldLabel(
        'postalCode',
        t('placeholders.postalCode', { context: 'Canada' }),
      ),
      setFieldLabel('state', t('placeholders.state', { context: 'Canada' })),
    ),
  );

  return R.pipe(
    React.Children.toArray,
    R.pipe(
      (address?.country ?? '') !== 'Canada'
        ? R.identity
        : setCustomTranslations,
      addAddressSearchButton,
    ),
  )(children);
};

export default WithAddressSearchButton;
